import React, { useState, useEffect } from "react";
import {X, CheckCircle, AlertCircle, TrendingUp, ChevronRight, Phone, Mail, Building2} from 'lucide-react';
import { lumi } from "../lib/lumi";

interface Question {
  _id: string;
  question: string;
  options: {
    text: string;
    score: number;
  }[];
  order: number;
  category?: string;
}

interface ResultText {
  _id: string;
  level: string;
  minScore: number;
  maxScore: number;
  title: string;
  message: string;
  recommendation: string;
  color: string;
}


interface QualificationQuizProps {
  onClose: () => void;
}

export function QualificationQuiz({ onClose }: QualificationQuizProps) {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [resultTexts, setResultTexts] = useState<ResultText[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [showResult, setShowResult] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [quizResponseId, setQuizResponseId] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    message: ""
  });

  useEffect(() => {
    fetchQuestions();
    fetchResultTexts();
  }, []);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const result = await lumi.entities.quiz_questions.list({
        filter: { active: true },
        sort: { order: 1 }
      });
      setQuestions(result.list || []);
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchResultTexts = async () => {
    try {
      const result = await lumi.entities.quiz_result_texts.list({
        sort: { minScore: 1 }
      });
      setResultTexts(result.list || []);
    } catch (error) {
      console.error('Error fetching result texts:', error);
    }
  };

  const handleAnswer = async (score: number) => {
    const newAnswers = [...answers, score];
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResult(true);
      // Enregistrer les réponses du quiz
      await saveQuizResponse(newAnswers);
    }
  };

  const saveQuizResponse = async (finalAnswers: number[]) => {
    // Calcul sur 100: moyenne des scores (0 = besoin critique, 100 = autonome)
    const totalScore = finalAnswers.reduce((sum, score) => sum + score, 0);
    const averageScore = questions.length > 0 ? totalScore / questions.length : 0;
    const result = getResultLevel(averageScore);

    try {
      const response = await lumi.entities.quiz_responses.create({
        answers: finalAnswers,
        totalScore,
        averageScore,
        resultLevel: result.level,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });

      if (response?._id) {
        setQuizResponseId(response._id);
        console.log("Quiz response saved:", response._id);
      }
    } catch (error) {
      console.error("Error saving quiz response:", error);
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmitContactRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const currentResult = getResultLevel(averageScore);

    try {
      await lumi.entities.contact_requests.create({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        message: formData.message || "",
        quizResponseId: quizResponseId,
        // Résultats du quiz intégrés dans le dossier
        quizScore: Math.round(averageScore),
        quizTotalScore: totalScore,
        quizResultLevel: currentResult.level,
        quizResultMessage: currentResult.message,
        quizResultRecommendation: currentResult.recommendation,
        quizResultColor: currentResult.color,
        quizAnswers: answers,
        status: "pending",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });

      // Afficher un message de succès et fermer
      alert("✅ Votre demande a été envoyée avec succès ! Un membre de notre équipe vous contactera sous 48h.");
      onClose();
    } catch (error) {
      console.error("Error submitting contact request:", error);
      alert("❌ Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalScore = answers.reduce((sum, score) => sum + score, 0);
  const averageScore = questions.length > 0 ? totalScore / questions.length : 0;

  // Score inversé: récupération des textes depuis la base de données
  const getResultLevel = (score: number) => {
    // Trouver le texte correspondant au score
    const matchingText = resultTexts.find(
      text => score >= text.minScore && score <= text.maxScore
    );

    if (matchingText) {
      return {
        level: matchingText.title,
        color: matchingText.color,
        icon: matchingText.color === 'green' ? CheckCircle : AlertCircle,
        message: matchingText.message,
        recommendation: matchingText.recommendation
      };
    }

    // Fallback si aucun texte ne correspond
    return {
      level: "Résultat",
      color: "blue",
      icon: AlertCircle,
      message: "Merci d'avoir complété le quiz. Un membre de notre équipe analysera vos réponses.",
      recommendation: "Nous vous contacterons pour discuter de vos besoins spécifiques."
    };
  };

  const getResultMessage = () => {
    return getResultLevel(averageScore);
  };

  const result = getResultMessage();
  const ResultIcon = result.icon;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 shadow-2xl">
          <p className="text-gray-700">Chargement du quiz...</p>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 shadow-2xl max-w-md">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Quiz non disponible</h3>
          <p className="text-gray-600 mb-6">Aucune question n'est actuellement configurée. Contactez l'administrateur.</p>
          <button onClick={onClose} className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg transition-all">
            Fermer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-teal-600 to-cyan-600 text-white p-6 rounded-t-2xl flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold">Quiz de Qualification RUGIR</h2>
            <p className="text-teal-100 text-sm mt-1">
              {showContactForm ? "Demande d'appel - Prise de température" : "Évaluez votre besoin d'accompagnement en pilotage d'entreprise"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors">

            <X className="w-6 h-6" />
          </button>
        </div>

        {!showResult ?
        <div className="p-8">
            {/* Progress Bar */}
            <div className="mb-8">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Question {currentQuestion + 1} sur {questions.length}</span>
                <span>{Math.round((currentQuestion + 1) / questions.length * 100)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                className="bg-gradient-to-r from-teal-600 to-cyan-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentQuestion + 1) / questions.length * 100}%` }} />

              </div>
            </div>

            {/* Question */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                {questions[currentQuestion].question}
              </h3>

              <div className="space-y-3">
                {questions[currentQuestion].options.map((option, index) =>
              <button
                key={index}
                onClick={() => handleAnswer(option.score)}
                className="w-full text-left p-4 border-2 border-gray-200 rounded-xl hover:border-teal-600 hover:bg-teal-50 transition-all group">

                    <div className="flex items-center justify-between">
                      <span className="text-gray-800 group-hover:text-teal-900">
                        {option.text}
                      </span>
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-teal-600 transition-colors" />
                    </div>
                  </button>
              )}
              </div>
            </div>
          </div> :
        !showContactForm ?
        <div className="p-8">
            {/* Result */}
            <div className="text-center mb-8">
              <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full ${
            result.color === 'red' ? 'bg-red-100' :
            result.color === 'orange' ? 'bg-orange-100' :
            'bg-green-100'} mb-4`
            }>
                <ResultIcon className={`w-10 h-10 ${
              result.color === 'red' ? 'text-red-600' :
              result.color === 'orange' ? 'text-orange-600' :
              'text-green-600'}`
              } />
              </div>
              
              <h3 className="text-3xl font-bold text-gray-900 mb-2">
                {result.level}
              </h3>
              
              <div className="inline-block bg-gray-100 px-6 py-3 rounded-full mb-6">
                <span className="text-gray-600">Votre score : </span>
                <span className={`font-bold text-2xl ${
              result.color === 'red' ? 'text-red-600' :
              result.color === 'orange' ? 'text-orange-600' :
              'text-green-600'}`
              }>
                  {Math.round(averageScore)}/100
                </span>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <p className="text-gray-700 leading-relaxed mb-4">
                {result.message}
              </p>
              <div className="bg-white border-l-4 border-teal-600 p-4 rounded">
                <p className="font-semibold text-gray-900 mb-2">💡 Notre recommandation :</p>
                <p className="text-gray-700">
                  {result.recommendation}
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
              onClick={() => setShowContactForm(true)}
              className="flex-1 bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-6 py-4 rounded-full font-semibold hover:shadow-lg hover:scale-105 transition-all flex items-center justify-center">

                <Phone className="w-5 h-5 mr-2" />
                Demander un appel
              </button>
              <button
              onClick={() => {
                setCurrentQuestion(0);
                setAnswers([]);
                setShowResult(false);
              }}
              className="flex-1 bg-white text-gray-800 px-6 py-4 rounded-full font-semibold border-2 border-gray-200 hover:border-teal-600 hover:shadow-lg transition-all">Relancer le quiz


            </button>
            </div>

            <p className="text-center text-sm text-gray-500 mt-6">
              Un membre de l'équipe RUGIR vous contactera sous 48h pour échanger sur vos besoins
            </p>
          </div> :

        <div className="p-8">
            {/* Contact Form */}
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Planifions votre prise de température</h3>
              <p className="text-gray-600">Remplissez ce formulaire et nous vous contacterons sous 48h pour un échange stratégique de 30 minutes.</p>
            </div>

            <form onSubmit={handleSubmitContactRequest} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prénom *
                  </label>
                  <input
                  type="text"
                  name="firstName"
                  required
                  value={formData.firstName}
                  onChange={handleFormChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-600 focus:outline-none transition-colors"
                  placeholder="Jean" />

                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom *
                  </label>
                  <input
                  type="text"
                  name="lastName"
                  required
                  value={formData.lastName}
                  onChange={handleFormChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-600 focus:outline-none transition-colors"
                  placeholder="Dupont" />

                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Email *
                </label>
                <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleFormChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-600 focus:outline-none transition-colors"
                placeholder="jean.dupont@entreprise.com" />

              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Téléphone *
                </label>
                <input
                type="tel"
                name="phone"
                required
                value={formData.phone}
                onChange={handleFormChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-600 focus:outline-none transition-colors"
                placeholder="+33 6 12 34 56 78" />

              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Building2 className="w-4 h-4 inline mr-1" />
                  Entreprise *
                </label>
                <input
                type="text"
                name="company"
                required
                value={formData.company}
                onChange={handleFormChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-600 focus:outline-none transition-colors"
                placeholder="Nom de votre entreprise" />

              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message (optionnel)
                </label>
                <textarea
                name="message"
                value={formData.message}
                onChange={handleFormChange}
                rows={4}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-600 focus:outline-none transition-colors resize-none"
                placeholder="Parlez-nous brièvement de vos enjeux actuels..." />

              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-6 py-4 rounded-full font-semibold hover:shadow-lg hover:scale-105 transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed">

                  {isSubmitting ?
                "Envoi en cours..." :

                <>
                      <TrendingUp className="w-5 h-5 mr-2" />
                      Envoyer ma demande
                    </>
                }
                </button>
                <button
                type="button"
                onClick={() => setShowContactForm(false)}
                className="flex-1 bg-white text-gray-800 px-6 py-4 rounded-full font-semibold border-2 border-gray-200 hover:border-teal-600 hover:shadow-lg transition-all">

                  Retour aux résultats
                </button>
              </div>
            </form>

            <div className="mt-6 p-4 bg-teal-50 border border-teal-200 rounded-xl">
              <p className="text-sm text-teal-800 text-center">
                🔒 Vos informations sont confidentielles et ne seront utilisées que pour vous contacter dans le cadre de votre demande d'accompagnement RUGIR.
              </p>
            </div>
          </div>
        }
      </div>
    </div>);

}